<template>
  <div id="app">
    <!-- 顶部Logo区域 -->
    <div class="bg-white h-24 md:h-28 flex items-center justify-between px-4 md:px-8">
      <div class="flex items-center cursor-pointer" @click="$router.push('/home')">
        <img src="@/assets/img/logo.png" alt="" class="h-16 md:h-24" />
      </div>
      <!-- Mobile contact buttons and menu -->
      <div class="flex items-center space-x-1 md:hidden">
        <!-- Email button -->
        <a href="mailto:info@atumu.co.jp"
          class="p-2 rounded-md border border-gray-300 hover:border-gray-400 transition-colors duration-200">
          <svg class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
        </a>
        <!-- Phone button -->
        <a href="tel:0668857660"
          class="p-2 rounded-md border border-gray-300 hover:border-gray-400 transition-colors duration-200">
          <svg class="h-6 w-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
        </a>
        <!-- Hamburger menu button -->
        <button @click="toggleMenu"
          class="text-gray-600 hover:text-gray-300 focus:outline-none p-2 rounded-md border border-gray-300 hover:border-gray-400 transition-colors duration-200">
          <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path v-if="!isMenuOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M4 6h16M4 12h16M4 18h16" />
            <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <!-- Desktop contact info -->
      <div class="hidden md:block text-right">
        <div class="flex items-center justify-end text-2xl">
          <i class="fas fa-phone-alt text-[#9C27B0] mx-2"><span class="ml-2">06-6838-3272</span></i>
        </div>
      </div>
    </div>

    <!-- PC导航菜单 -->
    <nav class="bg-[#222222] w-full hidden md:block">
      <ul class="flex justify-center items-stretch h-[50px] w-full">
        <li v-for="(item, index) in menuItems" :key="index" class="flex-1">
          <router-link :to="item.path"
            class="text-white border border-gray-600 h-full flex items-center justify-center font-bold relative overflow-hidden transition-all duration-300 group"
            active-class="active-nav-link">
            {{ item.name }}
            <span
              class="absolute bottom-0 left-0 w-full h-1 bg-primary transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-center"></span>
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- Mobile Menu -->
    <transition enter-active-class="transition duration-200 ease-out" enter-from-class="opacity-0 -translate-y-1"
      enter-to-class="opacity-100 translate-y-0" leave-active-class="transition duration-150 ease-in"
      leave-from-class="opacity-100 translate-y-0" leave-to-class="opacity-0 -translate-y-1">
      <div v-if="isMenuOpen" class="md:hidden">
        <!-- Mobile navigation -->
        <nav class="bg-[#222222]">
          <div class="px-2 py-3">
            <router-link v-for="(item, index) in menuItems" :key="index" :to="item.path"
              class="block text-white py-2 px-4 text-base font-medium hover:bg-gray-700 rounded transition-colors duration-200"
              active-class="bg-[#9C27B0] text-white font-bold" @click="closeMenu">
              {{ item.name }}
            </router-link>
          </div>
        </nav>
      </div>
    </transition>

    <!-- 主页面 -->
    <router-view />

    <!-- 页脚 -->
    <footer class="bg-gray-900 text-white mt-12">
      <div class="container mx-auto px-6 py-12">
        <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 class="text-xl font-bold mb-4">株式会社アトムコーポレーション</h3>
            <p class="text-gray-400">〒532-0036<br>大阪府大阪市淀川区三津屋中３－８－２０</p>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4">メニュー</h3>
            <ul class="space-y-2 text-gray-400">
              <li v-for="(item, index) in menuItems" :key="index">
                <router-link :to="item.path" class="hover:text-white transition-colors">{{ item.name }}</router-link>
              </li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4">連絡先</h3>
            <ul class="space-y-2 text-gray-400">
              <li>電話：06-6838-3272</li>
              <li>FAX：06-6838-3273</li>
              <li>携帯：080-6129-6699</li>
              <li>メール：info@atumu.co.jp</li>
            </ul>
          </div>
          <div>
            <h3 class="text-xl font-bold mb-4"></h3>
            <div class="flex space-x-4">
            </div>
          </div>
        </div>
        <div class="border-t border-gray-800 pt-8">
          <div class="text-center text-gray-400">
            <p>&copy; Copyright Atumu Co., Ltd. All Rights Reserved</p>
          </div>
        </div>
      </div>
    </footer>
  </div>
</template>

<script>
export default {
  data() {
    return {
      isMenuOpen: false,
      menuItems: [
        { name: 'ホーム', path: '/home' },
        { name: '会社情報', path: '/company' },
        { name: '中古買取', path: '/appliances' },
        { name: '切断加工', path: '/cutting' },
        { name: '越境貿易', path: '/ecommerce' },
        { name: '採用情報', path: '/invitation' },
        { name: '新着情報', path: '/news' },
        { name: 'お問い合わせ', path: '/contact' }
      ]
    };
  },
  methods: {
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    closeMenu() {
      this.isMenuOpen = false;
    }
  },
  watch: {
    $route() {
      this.closeMenu();
    }
  },
  mounted() {
    // Close menu when clicking outside
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.closeMenu();
      }
    });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.closeMenu);
  },
  created() {
  }
};
</script>

<style>
.nav-glass {
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
}

.active-nav-link {
  background-color: #9C27B0;
  /* primary color */
  border-color: #9C27B0 !important;
  position: relative;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
}

.active-nav-link::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 3px;
  background-color: white;
}

/* Add smooth transition for mobile menu */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s ease;
}

.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Prevent scrolling when mobile menu is open */
.menu-open {
  overflow: hidden;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .nav-link {
    padding: 0.75rem 1rem;
  }
}
</style>